import { useCallback, useEffect, useState } from "react";

import { getErrorMessage, isAPIError } from "@/core/services";
import type { EmbarcacaoDetailStatus } from "./embarcacoes.types";

export interface EmbarcacaoDetailSectionQuery<T> {
  readonly load: (params: { id: string; signal: AbortSignal }) => Promise<T | null>;
  readonly isEmpty: (data: T) => boolean;
  readonly messages: EmbarcacaoDetailSectionMessages;
}

export interface EmbarcacaoDetailSectionMessages {
  readonly sessionExpired: string;
  readonly forbidden: string;
  readonly notFound: string;
  readonly error: string;
}

export interface EmbarcacaoDetailSectionState<T> {
  status: EmbarcacaoDetailStatus;
  data: T | null;
  errorMessage: string | null;
}

const INITIAL_STATE: EmbarcacaoDetailSectionState<never> = {
  status: "loading",
  data: null,
  errorMessage: null,
};

const EMPTY_STATE: EmbarcacaoDetailSectionState<never> = {
  status: "empty",
  data: null,
  errorMessage: null,
};

export interface UseEmbarcacaoDetailSectionResult<T> extends EmbarcacaoDetailSectionState<T> {
  reload: () => void;
}

function toFailure(
  error: unknown,
  messages: EmbarcacaoDetailSectionMessages,
): EmbarcacaoDetailSectionState<never> {
  const status = isAPIError(error) ? error.status : null;

  if (status === 401)
    return { status: "sessionExpired", data: null, errorMessage: messages.sessionExpired };
  if (status === 403) return { status: "forbidden", data: null, errorMessage: messages.forbidden };
  if (status === 404) return { status: "notFound", data: null, errorMessage: messages.notFound };

  return {
    status: "error",
    data: null,
    errorMessage: getErrorMessage(error) || messages.error,
  };
}

export function useEmbarcacaoDetailSection<T>(
  id: string,
  query: EmbarcacaoDetailSectionQuery<T>,
): UseEmbarcacaoDetailSectionResult<T> {
  const [state, setState] = useState<EmbarcacaoDetailSectionState<T>>(INITIAL_STATE);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    setState(INITIAL_STATE);

    query
      .load({ id, signal: controller.signal })
      .then((data) => {
        if (cancelled) return;

        if (!data) {
          setState({ status: "notFound", data: null, errorMessage: query.messages.notFound });
          return;
        }

        setState(
          query.isEmpty(data) ? EMPTY_STATE : { status: "success", data, errorMessage: null },
        );
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setState(toFailure(error, query.messages));
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [id, query, reloadToken]);

  const reload = useCallback(() => setReloadToken((token) => token + 1), []);

  return { ...state, reload };
}
